import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import ReactHotkeys from 'react-hot-keys'
import { useTranslation } from 'react-i18next'

import Dropdown from '../../App/Components/Dropdown/Dropdown'
import Fireworks from '../../App/Components/Fireworks'
import Cookies from '../../App/Cookies'
import useLongPress from '../../App/Hooks/Login/useLongPress'
import useLocalStorage from '../../App/Hooks/useLocalStorage'
import { IEstablishment } from '../../Establishment/interface/IEstablishment'
import { IUser } from '../DataStore/IUser'

import Establishment from './Establishment/Establishment'
import Partner from './Partner/Partner'
import Password from './Password/Password'
import style from './style.scss'
import Users from './Users/Users'

const Login: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [isPartner, setIsPartner] = useState(
    window.location.href.indexOf('partner') > -1,
  )
  const [establishment, setEstablishment] =
    useLocalStorage<null | IEstablishment>('login_establishment', null)
  const [user, setUser] = useLocalStorage<IUser | null>('login_user', null)
  const [showFireworks, setShowFireworks] = useState<boolean>(false)

  useEffect(() => {
    // Clear all access and refresh tokens
    const cookies = Cookies.getAll()
    Object.keys(cookies).forEach(name => {
      if (
        name.startsWith('access_token.') ||
        name.startsWith('refresh_token.') ||
        name.startsWith('token_expired_at.')
      ) {
        Cookies.delete(name)
      }
    })

    if (window.location.search === '?p') {
      setIsPartner(!isPartner)
    }
  }, [])

  const title = useMemo<string>(() => {
    if (!establishment) {
      return t('Login')
    }

    return establishment.name
  }, [establishment, i18n.language])

  const onBackClicked = (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    setEstablishment(null)

    if (isPartner) {
      setIsPartner(false)
      return
    }

    if (user) {
      setUser(null)
      return
    }

    if (establishment) {
      setEstablishment(null)
      return
    }
  }

  const changeLanguage = (e: SyntheticEvent<HTMLFormElement>) => {
    i18n.changeLanguage(e.currentTarget.value)
  }

  const isOptios = () => {
    return window.location.hostname.indexOf('esthios') >= 0
  }

  const onLongPress = useCallback(() => {
    setIsPartner(!isPartner)
  }, [isPartner])

  const longPressEvent = useLongPress(onLongPress)

  return (
    <div className={style.loginContainer}>
      {showFireworks ? <Fireworks /> : null}
      <div
        className={`${style.loginBody}  ${
          isOptios ? style.bgOptios : style.bgEsthios
        }`}
      />
      <ReactHotkeys
        keyName={'shift+p,alt+p'}
        onKeyUp={() => setIsPartner(!isPartner)}>
        <ReactHotkeys
          keyName={'alt+f'}
          onKeyUp={() => setShowFireworks(!showFireworks)}>
          <img
            className="home-logo"
            src={'/img/new_logo.png'}
            alt={'Logo'}
            {...longPressEvent}
          />
          <h1 className={'text-white'}>{title}</h1>
          {user || establishment || isPartner ? (
            <a className="backButton text-white" onClick={onBackClicked}>
              &laquo; {t('Back')}
            </a>
          ) : null}
          <div className={style.loginWrapper}>
            {isPartner ? (
              <Partner />
            ) : user && establishment ? (
              <Password establishment={establishment} user={user} />
            ) : establishment ? (
              <Users
                establishment={establishment}
                onSelect={userData => setUser(userData)}
              />
            ) : (
              <Establishment
                onSubmit={establishmentData =>
                  setEstablishment(establishmentData)
                }
                onChangeLanguage={changeLanguage}
              />
            )}
          </div>
          {establishment ? null : (
            <>
              <Dropdown
                value={i18n.language}
                onChange={changeLanguage}
                className={style.language}>
                <option value="nl">Nederlands</option>
                <option value="fr">français</option>
                <option value="en">English</option>
              </Dropdown>
              <div className={style.noAccount}>
                <span>{t('No account yet?')}</span>{' '}
                <a href={`/${i18n.language}/signup`}>{t('Signup')}</a>
              </div>
            </>
          )}
          <p>
            {user ? (
              <div className={style.forgotten}>
                <a href="/forgot-password" className={'text-white'}>
                  {t('Forgot password')}
                </a>
              </div>
            ) : null}
          </p>
        </ReactHotkeys>
      </ReactHotkeys>
    </div>
  )
}

export default Login
